<template>
  <div v-mousedown-outside="() => (showResults = false)" class="root position-relative">
    <span class="input-wrapper">
      <input
        ref="inputRef"
        v-model.trim="searchModel"
        type="search"
        class="form-control"
        :class="{ 'offset-cancel-button': !includedExtraCountries }"
        :placeholder="placeholder || `${appTranslate('Quick search')} (${includedSearchFields})`"
        :title="capitalize(includedSearchFields)"
        @keydown="keyHandler"
        @click="showResults = true"
        @focus="showResults = true"
        @blur="showResults = false"
      />

      <template v-if="!includedExtraCountries">
        <div class="dropdown-toggle-wrapper">
          <a id="dropdownMenuButton" class="dropdown-toggle" type="button" data-toggle="dropdown">
            <img v-if="isDeviatingCulture && dataCulture" :src="getFlag(undefined, dataCulture.language)" class="language deviating-language" />
            <img v-if="selectedCountry" :src="getFlag(selectedCountry)" class="small-flag" />
          </a>
          <div class="dropdown-menu dropdown-menu-right">
            <button v-for="country of store.availableCountries" :key="country" class="dropdown-item btn d-block" @click="setCountry(country)">
              <img :src="getFlag(country)" class="flag small-flag" />
              {{ translate(country, 'countries', 'capitalize') }}
            </button>
          </div>
        </div>
      </template>
    </span>

    <div v-if="loading" style="position: absolute; top: 50%; right: 5px; width: 2rem; height: 2rem; transform: translateY(-50%)">
      <div class="spinner-border" style="border-width: 1px; width: 100%; height: 100%" />
    </div>

    <div v-show="searchResult?.searchString && showResults" ref="resultList" class="result-list" @scroll="handleScroll($event)" @mousedown.prevent>
      <template v-if="searchResult?.products?.results">
        <button
          v-if="includeCategories"
          class="d-flex link list-item"
          :class="{
            selected: selectedIndex === 0,
          }"
          @mouseenter="selectedIndex = 0"
          @mousedown.prevent.stop="showAllResults()"
          @click="showAllResults()"
        >
          <small class="font-weight-bold">
            {{ appTranslate('Products') }} ({{
              appTranslate('showing %s of %s hits', [searchResult?.products?.results, searchResult?.products?.total], 'lowercase')
            }})
          </small>

          <span v-if="loadMoreInline" class="matches list-item py-0 ml-auto">
            {{ appTranslate('%s hits', [searchResult.products.total]) }}
          </span>
          <span v-else class="link matches top ml-auto p-0">
            {{ appTranslate('Show all', [searchResult.products.total]) }}
          </span>
        </button>

        <button
          v-for="(product, index) of searchResult.products.data || []"
          :key="product.sku"
          class="list-item link product"
          :class="{
            selected: index === selectedIndex - selectedOffset,
          }"
          @click="select(product)"
          @mouseenter="selectedIndex = index + selectedOffset"
        >
          <ProductMiniCard :product="product" />
        </button>
        <!-- <button
          v-if="!loadMoreInline"
          class="link matches"
          :class="{
            selected: selectedIndex === searchResult.products.results + (loadMoreInline ? 0 : 1),
          }"
          @click="showAllResults()"
          @mousedown.prevent.stop="showAllResults()"
          @mouseenter="selectedIndex = searchResult.products.results + (loadMoreInline ? 0 : 1)"
        >
          {{ translate('Show all %s hits', 'application', 'capitalize', undefined, [searchResult.products.total]) }}
        </button> -->
        <button
          v-if="loadMoreInline && !useInfiniteScroll && !allProductResultsLoaded"
          class="list-item link matches"
          :class="{
            selected: selectedIndex === searchResult.products.results,
          }"
          @click="loadMoreProducts()"
          @mousedown.prevent.stop="loadMoreProducts()"
          @mouseenter="selectedIndex = searchResult.products.results"
        >
          {{ appTranslate('Show more') }}
        </button>
        <div v-if="loadingMore" class="d-flex justify-content-center py-2">
          <LoaderComponent static size="medium" />
        </div>
      </template>

      <hr v-if="searchResult?.products?.results && searchResult?.categories?.data?.length" class="p-0" />

      <template v-if="searchResult?.categories?.data?.length">
        <small class="font-weight-bold list-item d-flex">
          {{ translate('Categories', 'taxonomies', 'capitalize') }} ({{ appTranslate('%s hits', [searchResult?.categories?.data?.length]) }})</small
        >

        <button
          v-for="(category, index) of searchResult?.categories?.data"
          :key="category.id"
          class="list-item link category"
          :class="{
            selected: index + productResults === selectedIndex,
          }"
          @click="filterOnCategory(category.id)"
          @mouseenter="selectedIndex = index + (searchResult.products?.results || 0) + selectedOffset"
        >
          <span v-if="category.parentName" class="parent mr-1">{{ category.parentName }} &raquo;</span>{{ category.name }}
          <small class="font-weight-bold ml-auto">({{ appTranslate('%s products', [category.matches ?? 0]) }})</small>
        </button>
      </template>

      <p v-if="!searchResult?.products?.results && !searchResult?.categories?.data?.length && searchString" class="list-item mb-0">
        {{ appTranslate('No hits') }}
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LoaderComponent from '@/components/simple/LoaderComponent.vue'
import ProductMiniCard from '@/components/simple/ProductMiniCard.vue'
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { SearchResult } from '@/models/Filter'
import { Country, Culture } from '@/models/Global'
import { SimplifiedProduct } from '@/models/Product'
import DataService from '@/services/DataService'
import { useDefaultStore } from '@/store'
import { Debouncer } from '@/utils/Debouncer'
import { clamp, isScrolledToBottom, sortLexioNumerically } from '@/utils/tools'
import { capitalize, computed, ref, watch } from 'vue'

// -------------
// -- Statics --
// -------------
interface Props {
  loadMoreInline?: boolean
  modelValue?: string
  placeholder?: string
  includeCultureExclusiveData?: boolean
  closeOnSelect?: boolean
  dataCulture: Culture | null
  includedExtraCountries?: Iterable<Country>
  includeCategories?: boolean
  includeVersions?: boolean
}

const useInfiniteScroll = true

const defaultInputDelayMs = 700
const minimumCharsToSearch = 2

const searchDebouncer = new Debouncer()

// -----------
// -- Setup --
// -----------
const props = withDefaults(defineProps<Props>(), {
  closeOnSelect: true,
})

const emit = defineEmits(['select', 'filter', 'update:modelValue'])

const store = useDefaultStore()
const { translate, appTranslate } = useTranslation()
const { getFlag, setDropdownDirection } = useTools()

// -------------------
// -- Reactive vars --
// -------------------
const resultList = ref<HTMLElement>()
const inputRef = ref<HTMLElement>()

const loading = ref(false)
const loadingMore = ref(false)
const searchResult = ref<SearchResult>()
const showResults = ref(false)
const selectedIndex = ref(-1)
const searchString = ref('')

const selectedCountry = ref(props.dataCulture?.country)

// --------------------
// -- Computed props --
// --------------------
const resultLimit = computed(() => (props.loadMoreInline ? 10 : 5))

const allProductResultsLoaded = computed(() => (searchResult.value?.products?.results || 0) >= (searchResult.value?.products?.total || 0))

const includedSearchFields = computed(() => {
  const fields = [translate('item no', 'attributes'), translate('name', 'attributes'), 'EAN', translate('recurrence id', 'attributes')]

  if (props.includeCategories) {
    fields.push(translate('categories', 'taxonomies'))
  }

  return fields.join(' / ')
})

const isDeviatingCulture = computed(() =>
  props.dataCulture?.country ? store.availableCulturesByCountry.get(props.dataCulture.country)?.language !== props.dataCulture.language : false,
)

// const includedExtraCountries = computed(() => {
//   const isDeviatingCulture = props.specificCulture?.country
//     ? store.availableCulturesByCountry[props.specificCulture.country]?.language !== props.specificCulture.language
//     : false

//   if (isDeviatingCulture) {
//     // include countries using the data language when deviating cultures are used (sometimes used in presentations)
//     return store.availableCultures?.filter((culture) => culture.language === props.specificCulture?.language).map((culture) => culture.country)
//   }
//   return undefined
// })

const implicitlySelectedLanguage = computed(() => (selectedCountry.value ? store.availableCulturesByCountry.get(selectedCountry.value)?.language : undefined))

const productResults = computed(() => (searchResult.value?.products?.results ?? 0) + (!props.loadMoreInline && searchResult.value?.products?.results ? 1 : 0))

const selectedOffset = computed(() => (!props.loadMoreInline && searchResult.value?.products?.results ? 1 : 0))

// --------------
// -- Watchers --
// --------------
watch(
  () => props.dataCulture,
  () => {
    selectedCountry.value = props.dataCulture?.country
  },
  { deep: true },
)

watch(
  () => [props.dataCulture, props.includedExtraCountries],
  () => {
    searchResult.value = undefined
  },
  { deep: true },
)

watch(showResults, () => {
  if (inputRef.value && resultList.value) {
    setDropdownDirection(inputRef.value, resultList.value)
  }

  if (!searchResult.value) {
    search()
  }
})

watch(searchString, (value) => {
  showResults.value = !!value
})

// -------------
// -- Methods --
// -------------
const hideResults = () => {
  showResults.value = false
  inputRef.value?.blur() // was earlier used to close the results. also ensures that keydown events aren't blocked
}

const navigate = (offset: number, e: Event) => {
  if (showResults.value) {
    const max = Array.from(resultList.value?.children || []).filter((child) => child.nodeName === 'BUTTON').length - 1
    if (max > 0) {
      e.preventDefault()
    }

    selectedIndex.value = clamp(selectedIndex.value + offset, 0, max)
    getSelectedItem()?.scrollIntoView({ block: 'nearest' })
  }
}

const keyHandler = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    const selectedItem = getSelectedItem()
    if (selectedItem) {
      selectedItem.click()
    } else if (!props.loadMoreInline) {
      showAllResults()
    }
  } else if (e.key === 'ArrowUp') {
    showResults.value = true
    navigate(-1, e)
  } else if (e.key === 'ArrowDown') {
    navigate(1, e)
    showResults.value = true
  } else if (e.key === ' ' && e.ctrlKey) {
    showResults.value = true
  } else if (e.key === 'Escape') {
    searchResult.value = undefined
    if (searchString.value) {
      e.stopPropagation()
    }
  } else {
    e.stopPropagation()
  }
}

const handleScroll = async (e: Event) => {
  if (useInfiniteScroll && props.loadMoreInline && !allProductResultsLoaded.value && !loadingMore.value) {
    if (isScrolledToBottom(e.target as HTMLDivElement)) {
      loadingMore.value = true
      await loadMoreProducts()
      loadingMore.value = false
    }
  }
}

const select = (product: SimplifiedProduct) => {
  emit('update:modelValue', product.sku)
  emit('select', {
    value: product,
  })

  if (props.closeOnSelect) {
    hideResults()
  }
}

const getSelectedItem = () => {
  const selectableItems = Array.from(resultList.value?.children || []).filter((child) => child.nodeName === 'BUTTON')
  return selectableItems[selectedIndex.value] as HTMLButtonElement
}

const searchModel = computed({
  get: () => searchString.value,
  set: (value: string) => {
    searchString.value = value

    if (value) {
      searchDebouncer.debounce(search, defaultInputDelayMs)
    } else {
      searchResult.value = undefined
    }
  },
})

const search = async (showResultsImmediately = true) => {
  if (searchString.value.length >= minimumCharsToSearch) {
    loading.value = true
    try {
      const searchResponse = await getMatches()

      if (searchString.value === searchResponse?.searchString) {
        sortLexioNumerically(searchResponse?.categories?.data, 'parentName', 'asc', 'desc', 2000, 'name')

        searchResult.value = { ...searchResult.value, ...searchResponse }

        if (showResultsImmediately) {
          inputRef.value?.focus()
          if (inputRef.value && resultList.value) {
            setDropdownDirection(inputRef.value, resultList.value)
          }
          selectedIndex.value = 0
          // selectedIndex.value = Math.min(selectedIndex.value, (searchResult.value.products?.results ?? 0) + (searchResult.value.categories?.results ?? 0))
        }
      }
    } finally {
      loading.value = false
    }
  }
}

const loadMoreProducts = async () => {
  if (searchResult.value?.products) {
    const response = await getMatches(searchResult.value?.products?.results)

    if (response?.products.results) {
      searchResult.value.products.data = searchResult.value.products.data.concat(...response.products.data)
      searchResult.value.products.results += response.products.results
      searchResult.value.products.total = response.products.total
    }
  }
}

const getMatches = (productsFrom?: number) => {
  // use language from prop if specificCulture isn't an available culture, otherwise match the language with the country
  const dataLanguage = isDeviatingCulture.value ? props.dataCulture?.language || store.dataCulture?.language : implicitlySelectedLanguage.value

  return DataService.search(searchString.value, productsFrom, resultLimit.value, productsFrom ? false : props.includeCategories, props.includeVersions, {
    dataCountry: selectedCountry.value,
    dataLanguage,
    includeCultureExclusiveData: props.includeCultureExclusiveData,
    includedCountries: props.includedExtraCountries ? Array.from(props.includedExtraCountries) : selectedCountry.value ? [selectedCountry.value] : undefined,
  })
}

const showAllResults = () => {
  emit('filter', {
    _q: searchString.value,
  })
  showResults.value = false
}

const filterOnCategory = (id: string) => {
  emit('filter', {
    _categories: id,
  })
  showResults.value = false
}

const setCountry = (newCountry: Country) => {
  selectedCountry.value = newCountry
  // if (showResults.value) {
  //   search(false)
  // } else {
  //   searchResult.value = undefined
  // }
  searchResult.value = undefined
}
</script>

<style lang="scss" scoped>
.loader {
  position: absolute;
  right: 21px;
  top: 50%;
  opacity: 0.7;
  transform: scale(1.5);
}

.root {
  position: relative;

  input,
  .result-list {
    font-size: 0.9rem;
  }

  input {
    width: 100%;
    height: 38px;

    &.offset-cancel-button::-webkit-search-cancel-button {
      position: absolute;
      right: 50px;
    }
  }

  .input-wrapper {
    position: relative;
    display: flex;
    align-items: center;

    .dropdown-toggle-wrapper {
      position: absolute;
      right: 5px;
    }
  }

  .result-list {
    position: absolute;
    width: 100%;
    min-width: 300px;

    z-index: 99;
    max-height: 250px;
    overflow-y: auto;
    // overflow-x: hidden;
    background-color: white;
    border: 1px solid #ccc;
    box-shadow: 1px 2px 8px #0004;

    overscroll-behavior: contain;

    &.reverse {
      box-shadow: 1px -2px 8px #0004;
      bottom: 2.5em;
    }

    .list-item {
      padding: 0.5em;
      //   text-align: left;
    }

    // .product,
    // .category
    button {
      // padding: 0.5em;
      text-align: left;

      display: flex;
      flex-wrap: wrap;
      width: 100%;
    }

    .selected {
      background-color: var(--light-grey);
    }

    .matches {
      display: block;
      font-size: 0.9em;
      font-style: italic;
      padding: 1em 1em;

      &.top {
        padding-block: 0.5em;
        text-align: left;
      }
    }

    .parent {
      color: #aaa;
      font-size: 0.8em;
    }
  }
}

.deviating-language {
  position: absolute;
  transform: translate(30%, -50%) scale(0.8);
  opacity: 0.75;
}
</style>
